import { z } from "zod";

/**
 * ARAMA (CLOS-183 `[165]`, model: ADR-0036).
 *
 * Üst bar araması sözleşme, şablon ve müvekkil kayıtlarını tek sorguda buluyor. Sonuç
 * satırı bir KAYDA GİDEN KAPI: kendi başına veri taşımıyor, hedefe götürüyor ve hedef
 * açılırken erişim yeniden soruluyor (CLOS-93).
 */

/**
 * Aranabilir kayıt türleri.
 *
 * ⚠ Bu liste bir SÖZLEŞME, bir tavan değil: yeni bir tür sunucuda
 * `registerSearchProvider` ile geliyor ve buraya da yazılıyor. İkisi ayrışırsa sunucu
 * kayıtlı olmayan türü zaten düşürüyor (fail-closed) — arayüz görmediği bir tür için
 * filtre çizmiyor.
 */
export const SEARCH_RECORD_TYPES = ["contract", "template", "client"] as const;
export type SearchRecordType = (typeof SEARCH_RECORD_TYPES)[number];

/** Kullanıcıya görünen tür adları — `docs/glossary.md` ile birebir (kriter 20). */
export const SEARCH_RECORD_TYPE_NAMES: Record<SearchRecordType, string> = {
  contract: "Sözleşme",
  template: "Şablon",
  client: "Müvekkil",
};

export const searchConfigSchema = z.object({
  version: z.literal(1),
  /**
   * Bu uzunluğun altındaki sorgu ÇALIŞTIRILMAZ.
   *
   * ⚠ Tek harflik bir prefix (`a:*`) indeksin neredeyse tamamıyla eşleşir: hem yavaş, hem
   * kullanıcıya anlamsız bir liste. Sınır bir performans ayarı değil, ürün davranışı.
   */
  minQueryLength: z.number().int().min(1),
  /** Üst sınır: uzun bir sorgu, `tsquery` üretimini gereksiz yere büyütür. */
  maxQueryLength: z.number().int().min(1),
  /**
   * Tür başına sonuç tavanı.
   *
   * ⚠ Toplam tavandan AYRI ve bu bilinçli: tek tavan olsaydı kalabalık bir tür (bin
   * sözleşme) ötekileri (üç şablon) listeden tamamen silerdi. Kart *"bir türde erişilebilir
   * kayıt bulunmaması diğer türlerin sonuçlarını bloke etmez"* diyor; tersi de doğru
   * olmalı — bir türün bolluğu ötekini bastırmamalı.
   */
  perTypeLimit: z.number().int().min(1),
  /** Toplam sonuç tavanı. */
  resultLimit: z.number().int().min(1),
  /**
   * Sorgu düzeyinde zaman aşımı (ms).
   *
   * ⚠ Aşılırsa arama BOŞ değil HATA döner. Sessizce boş dönmek, kullanıcıya "böyle bir
   * kayıt yok" demektir ve bu yanlış bir cevaptır — ürünün en pahalı kusur sınıfı
   * (sessiz başarı) tam olarak budur.
   */
  queryTimeoutMs: z.number().int().min(1),
});

export type SearchConfig = z.infer<typeof searchConfigSchema>;

/**
 * Sıralama ekseni (CLOS-185 `[167]`).
 *
 * ⚠ SERBEST METİN DEĞİL, KAPALI KÜME. Sıralama alanı istemciden gelen bir dize olsaydı
 * (`?sort=title`), o dize bir gün SQL'e yol bulurdu ya da indekslenmemiş bir kolona
 * sıralama yaptırırdı. Kart: *"URL'den gelen tür, filtre, sıralama ve hedef kimliği
 * güvenilir kabul edilmez."*
 *
 * `relevance` eşleşme gücüne göre (varsayılan), `recent` son güncellenene göre.
 */
export const SEARCH_SORTS = ["relevance", "recent"] as const;
export type SearchSort = (typeof SEARCH_SORTS)[number];

export const SEARCH_SORT_NAMES: Record<SearchSort, string> = {
  relevance: "İlgiye göre",
  recent: "Son güncellenen",
};

/** Arama sorgusu — üst bar ve sonuç ekranı aynı ucu kullanıyor. */
export const searchQuerySchema = z.object({
  q: z.string().trim().min(1, "Arama terimi gereklidir"),
  /**
   * Tür daraltması. Verilmezse tüm izinli türlerde aranır.
   *
   * ⚠ Bilinmeyen bir tür `VALIDATION` ile reddediliyor, sessizce yok sayılmıyor:
   * yok sayılsaydı istemci daralttığını sanırken tüm türlerde arama yapardı.
   */
  type: z.enum(SEARCH_RECORD_TYPES).optional(),
  /**
   * Durum daraltması (CLOS-185 `[167]`).
   *
   * ⚠ Değer İNDEKSTEKİ `filters.status` ile karşılaştırılıyor, domain tablosuyla değil.
   * Kart: *"filtreler ... sunucu tarafında çalışır"* ve *"sıralama ve filtreleme sunucu
   * tarafında tenant/permission/kayıt erişimi uygulanmış veri kümesi üzerinde çalışır."*
   *
   * ⚠ Her tür bu boyutu desteklemek zorunda DEĞİL (şablonun durumu indekse yazılmıyor,
   * çünkü pasif şablon zaten indeksten çıkıyor). Desteklemeyen türde filtre hiçbir satırla
   * eşleşmez — sessizce yok sayılmaz. Kart: *"desteklenmeyen boyut diğer arama türlerini
   * engellemez."*
   */
  status: z.string().trim().min(1).max(40).optional(),
  sort: z.enum(SEARCH_SORTS).optional(),
});

export type SearchQueryInput = z.infer<typeof searchQuerySchema>;

/**
 * Tek bir arama sonucu.
 *
 * ⚠ SNIPPET YOK ve bu kartın *"hassas içerik snippet'te sızmaz"* kriterinin karşılığı
 * (ADR-0036): sonuç satırı yalnız sağlayıcının GÜVENLİ ALAN olarak beyan ettiği başlığı
 * ve etiketleri taşıyor. Eşleşmenin metnin neresinde olduğu gösterilmiyor — çünkü o
 * metnin bir kısmı yetkisiz göze açık olabilir.
 */
export type SearchHit = {
  recordType: SearchRecordType;
  recordId: string;
  /** Kullanıcıya gösterilen güvenli başlık. */
  title: string;
  /** Kayda giden yol (`/app/sozlesmeler/:id`). Hedef açılırken erişim YENİDEN sorulur. */
  href: string;
  /** Tür etiketi ve varsa durum etiketi — filtre değerinden hassas bilgi türetilmiyor. */
  labels: string[];
};

/**
 * Tür başına GÖSTERİLEN sonuç sayısı (CLOS-185 `[167]`).
 *
 * ⚠ "Toplam kaç kayıt var" DEĞİL, "kaç tanesini görüyorsun". Gerçek toplam, kullanıcının
 * erişemediği kayıtları da sayardı ve kartın *"filtre seçenekleri yetkisiz kayıt bilgisi
 * sızdırmaz"* kriterini ihlal ederdi: sayacın büyüklüğü, göremediğiniz kayıtların varlığını
 * ele verir. Sayaç listelenen satır sayısıyla birebir aynı — kartın *"sıralama sonuç
 * sayaçlarıyla tutarlıdır"* kriteri bu.
 */
export type SearchCounts = Partial<Record<SearchRecordType, number>>;

export type SearchResult = {
  hits: SearchHit[];
  counts: SearchCounts;
  /**
   * Sorgunun tavana takılıp takılmadığı.
   *
   * ⚠ Kesilen liste "hepsi bu" gibi okunmamalı; arayüz bunu söylemek zorunda. Sessiz
   * kırpma, kullanıcının aradığı kaydın var olmadığına inanmasına yol açar.
   */
  truncated: boolean;
};
